import { z } from 'zod';
import type { IGameState, IStage } from '.';

export const teamKleurenPlayerColors = ['red', 'yellow', 'green', 'blue', 'orange'];

export const PersonalityColors = ['green', 'red', 'blue', 'yellow'] as const;
export type PersonalityColor = (typeof PersonalityColors)[number];

export const BoardPanelColors = ['yellow', 'red', 'green', 'blue', 'purple', 'cyan'] as const;
export type BoardPanelColor = (typeof BoardPanelColors)[number];

const TeamKleurenCardSchema = z.object({ name: z.string(), color: z.enum(PersonalityColors) });
export type TeamKleurenCard = z.infer<typeof TeamKleurenCardSchema>;

export const TeamKleurenColorQuizSchema = z.object({ question: z.string(), color: z.enum(PersonalityColors) });
export type TeamKleurenColorQuiz = z.infer<typeof TeamKleurenColorQuizSchema>;

export const TeamKleurenTextQuizSchema = z.object({
	question: z.string(),
	correctAnswer: z.number().min(0).max(3),
	answers: z.tuple([z.string(), z.string(), z.string(), z.string()]),
});
export type TeamKleurenTextQuiz = z.infer<typeof TeamKleurenTextQuizSchema>;

export const TeamKleurenDataSchema = z
	.object({
		goodCards: z.array(TeamKleurenCardSchema).default([]),
		badCards: z.array(TeamKleurenCardSchema).default([]),
		colorQuizQuestions: z.array(TeamKleurenColorQuizSchema).default([]),
		textQuizzes: z.array(TeamKleurenTextQuizSchema).default([]),
	})
	.default({ badCards: [], goodCards: [], colorQuizQuestions: [] });

export type Stage = IStage | 'play-rounds' | 'play-ending-game';

export type ColorData = {
	color: PersonalityColor;
	trait: string;
	details: string;
};

export type TeamKleurenPlayerState = {
	id: string;
	/**
	 * when false this player will get skipped by game logic,
	 * allowing them to leave and come back at a later time without blocking the entire game
	 */
	active: boolean;
	/**
	 * when a player has 4 good cards and 4 bad cards they get a +2 score increase.
	 * This should only happend once, so we keep track of wether we granted the points here.
	 */
	gotFullCardPoints: boolean;
	name: string;
	color: string;
	score: number;
	goodCards: TeamKleurenCard[];
	goodCardsBonus: boolean;
	badCards: TeamKleurenCard[];
	badCardsBonus: boolean;
	personality1: PersonalityColor;
	personality2: PersonalityColor;
	lastScreen: TeamKleurenUpdate;
};

export type TeamKleurenState = {
	stage: Stage;
	roundIndex: number;
	lastRound: boolean;

	goodCardPile: TeamKleurenCard[];
	badCardPile: TeamKleurenCard[];
	textQuiPile: TeamKleurenTextQuiz[];
	colorQuizPile: TeamKleurenColorQuiz[];

	currentGameColor?: BoardPanelColor;

	players: TeamKleurenPlayerState[];
	currentPlayerIndex: number;

	// card picking game
	cardPickStartPlayer: number;
	cardPickCurrentPlayer: number;
	cardOptions: { name: string; color: PersonalityColor; taken: boolean }[];
};

export const ProfileSchema = z.object({
	name: z.string(),
	color: z.string(),
	personality1: z.enum(PersonalityColors),
	details1: z
		.object({
			color: z.enum(PersonalityColors),
			trait: z.string(),
			details: z.string(),
		})
		.optional(),
	personality2: z.enum(PersonalityColors),
	details2: z
		.object({
			color: z.enum(PersonalityColors),
			trait: z.string(),
			details: z.string(),
		})
		.optional(),
	goodCards: z.array(TeamKleurenCardSchema),
	badCards: z.array(TeamKleurenCardSchema),
});
export type Profile = z.infer<typeof ProfileSchema>;

export const TeamKleurenUpdateSchema = z.discriminatedUnion('kind', [
	z.object({ kind: z.literal('show-login-screen'), failed: z.boolean().optional() }),
	z.object({ kind: z.literal('show-register-screen') }),
	z.object({ kind: z.literal('show-wait-screen'), playerName: z.string() }),
	z.object({ kind: z.literal('show-turnstart-screen') }),
	z.object({
		kind: z.literal('show-choosecard-screen'),
		forColor: z.enum(['red', 'yellow']),
		activePlayerName: z.string().optional(),
		options: z.array(z.object({ name: z.string(), taken: z.boolean() })),
		swapOptions: z.array(z.string()).optional(),
	}),
	z.object({
		kind: z.literal('show-colorquiz-screen'),
		question: z.string(),
		correctAnswer: z.enum(['red', 'yellow', 'green', 'blue']),
		timerFrom: z.number(),
		timerTo: z.number(),
	}),
	z.object({ kind: z.literal('show-answer-screen'), answer: z.string(), selectedAnswer: z.string().optional() }),
	z.object({
		kind: z.literal('show-answer-screen-colors'),
		answer1: z.enum(PersonalityColors),
		answer2: z.enum(PersonalityColors),
	}),

	z.object({
		kind: z.literal('show-textquiz-screen'),
		question: z.string(),
		correctAnswer: z.number().min(0).max(3),
		answers: z.tuple([z.string(), z.string(), z.string(), z.string()]),
		timerFrom: z.number(),
		timerTo: z.number(),
	}),
	z.object({
		kind: z.literal('show-randomevent-screen'),
		action: z.string(),
		playerPicker: z.boolean(),
		players: z.array(z.object({ name: z.string(), color: z.string() })),
	}),
	z.object({
		kind: z.literal('show-steal-screen'),
		players: z.array(
			z.object({
				name: z.string(),
				color: z.string(),
				goodCards: z.array(z.string()),
				badCards: z.array(z.string()),
			}),
		),
		goodCards: z.array(z.string()),
		badCards: z.array(z.string()),
	}),
	z.object({ kind: z.literal('show-endgamerounds-screen') }),
	z.object({
		kind: z.literal('show-profilequiz-screen'),
		question: z.string(),
		goodCards: z.array(z.string()),
		badCards: z.array(z.string()),
		correctAnswer1: z.enum(PersonalityColors),
		correctAnswer2: z.enum(PersonalityColors),
	}),
	z.object({
		kind: z.literal('show-profile-screen'),
		profile: ProfileSchema,
		reportUrl: z.string(),
	}),

	z.object({
		kind: z.literal('update-players-state'),
		players: z.array(z.object({ name: z.string(), color: z.string() })),
	}),
	z.object({
		kind: z.literal('update-player-state'),
		player: z.object({
			//
			name: z.string(),
			color: z.string(),
			score: z.number(),
			goodCards: z.array(TeamKleurenCardSchema),
			badCards: z.array(TeamKleurenCardSchema),
		}),
	}),

	z.object({ kind: z.literal('trigger-effect'), effect: z.enum(['confetti', 'red-flash']), text: z.string().optional() }),
]);
export type TeamKleurenUpdate = z.infer<typeof TeamKleurenUpdateSchema>;

export const TeamKleurenAdminUpdateSchema = z.discriminatedUnion('kind', [
	//
	z.object({ kind: z.literal('update-state'), state: z.any() }),
]);
export type TeamKleurenAdminUpdate = z.infer<typeof TeamKleurenAdminUpdateSchema>;

export const TeamKleurenMessageSchema = z.discriminatedUnion('kind', [
	// TODO: make it so this can only be sent by admin
	z.object({ kind: z.literal('start-game') }),
	z.object({ kind: z.literal('force-restart-round') }),
	z.object({ kind: z.literal('set-is-last-round'), lastRound: z.boolean() }),
	z.object({ kind: z.literal('set-player-active'), playerId: z.string(), active: z.boolean() }),

	// TODO: make it so this can be sent by any player
	z.object({ kind: z.literal('update-player-data'), playerId: z.string(), name: z.string(), color: z.string() }),
	z.object({ kind: z.literal('remove-player-data'), playerId: z.string() }),
	z.object({ kind: z.literal('choose-landing-spot'), playerId: z.string(), panelColor: z.enum(BoardPanelColors) }),
	z.object({ kind: z.literal('choose-card'), playerId: z.string(), card: z.string(), swapFor: z.string().optional() }),
	z.object({ kind: z.literal('choose-colorquiz-answer'), playerId: z.string(), color: z.enum(PersonalityColors) }),
	z.object({ kind: z.literal('choose-textquiz-answer'), playerId: z.string(), answer: z.number() }),
	// NOTE: we pass the color instead of the id because otherwise they could start impersonating the other players using their id to send messages
	z.object({ kind: z.literal('choose-randomevent-target'), playerId: z.string(), targetPlayerColor: z.string() }),
	z.object({
		kind: z.literal('choose-steal-target'),
		playerId: z.string(),
		targetPlayerColor: z.string(),
		targetPlayerCard: z.string(),
		isGoodCard: z.boolean(),
		swapFor: z.string().optional(),
	}),

	z.object({
		kind: z.literal('choose-profilequiz-answer'),
		playerId: z.string(),
		color1: z.enum(PersonalityColors),
		color2: z.enum(PersonalityColors),
	}),
]);
export type TeamKleurenMessage = z.infer<typeof TeamKleurenMessageSchema>;
